import { Link } from "react-router-dom";
import { MapPin, Phone, Mail } from "lucide-react";
import { ROUTES, CONTACT, INDIA_OFFICE } from "../data/content";
import { useLanguage } from "../i18n/LanguageContext";

export const Footer = () => {
  const { t, isAr } = useLanguage();
  const company = isAr ? CONTACT.companyAr : CONTACT.company;
  const address = isAr ? CONTACT.addressAr : CONTACT.address;

  return (
    <footer data-testid="site-footer" className="border-t border-amber-500/15 bg-[#060D16]">
      <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="flex items-center gap-3">
              <img src="/assets/logo-mark.png" alt="Al Nama Importer logo" className="h-12 w-auto object-contain" />
              <div>
                <p className="font-serif text-lg font-semibold text-emerald-50">{company}</p>
                <p className="text-[10px] uppercase tracking-[0.28em] text-amber-400">
                  {isAr ? "نربط أسواق الغذاء العالمية" : "Connecting Global Food Markets"}
                </p>
              </div>
            </div>
            <p className="mt-5 text-sm font-light leading-relaxed text-slate-400">{t("footer.blurb")}</p>
            <p className="mt-4 text-xs uppercase tracking-[0.2em] text-slate-500">{t("footer.rakez")}</p>
          </div>

          <div>
            <p className="font-display text-xs font-semibold uppercase tracking-[0.25em] text-amber-400">
              {t("footer.navigate")}
            </p>
            <ul className="mt-5 space-y-3">
              {ROUTES.map((r) => (
                <li key={r.to}>
                  <Link
                    to={r.to}
                    data-testid={`footer-link-${r.key}`}
                    className="text-sm text-slate-400 transition-colors hover:text-amber-400"
                  >
                    {r.key === "contact" ? t("footer.contactUs") : t(`nav.${r.key}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="font-display text-xs font-semibold uppercase tracking-[0.25em] text-amber-400">
              {t("footer.contactT")}
            </p>
            <ul className="mt-5 space-y-4 text-sm text-slate-400">
              <li className="flex gap-3">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-amber-500/70" />
                <span>
                  <span className="mb-1 block text-[10px] uppercase tracking-[0.2em] text-amber-400/80">
                    {t("contactPage.uaeOffice")}
                  </span>
                  {address}
                </span>
              </li>
              <li className="flex gap-3">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-amber-500/70" />
                <span>
                  <span className="mb-1 block text-[10px] uppercase tracking-[0.2em] text-amber-400/80">
                    {t("contactPage.indiaOffice")} · {INDIA_OFFICE.label}
                  </span>
                  {isAr ? INDIA_OFFICE.addressAr : INDIA_OFFICE.address}
                </span>
              </li>
              <li>
                <a href={CONTACT.phoneHref} data-testid="footer-phone-link" className="flex gap-3 transition-colors hover:text-amber-400" dir="ltr">
                  <Phone className="mt-0.5 h-4 w-4 shrink-0 text-amber-500/70" />
                  {CONTACT.phone}
                </a>
              </li>
              <li>
                <a href={INDIA_OFFICE.phoneHref} data-testid="footer-india-phone-link" className="flex gap-3 transition-colors hover:text-amber-400" dir="ltr">
                  <Phone className="mt-0.5 h-4 w-4 shrink-0 text-amber-500/70" />
                  {INDIA_OFFICE.phone}
                </a>
              </li>
              <li>
                <a href={CONTACT.emailHref} data-testid="footer-email-link" className="flex gap-3 transition-colors hover:text-amber-400">
                  <Mail className="mt-0.5 h-4 w-4 shrink-0 text-amber-500/70" />
                  {CONTACT.email}
                </a>
              </li>
            </ul>
          </div>

          <div>
            <p className="font-display text-xs font-semibold uppercase tracking-[0.25em] text-amber-400">
              {t("footer.productsT")}
            </p>
            <p className="mt-5 text-sm font-light leading-relaxed text-slate-400">{t("footer.productsList")}</p>
            <ul className="mt-6 space-y-3">
              <li>
                <Link to="/privacy-policy" data-testid="footer-privacy-link" className="text-sm text-slate-500 transition-colors hover:text-amber-400">
                  {t("footer.privacy")}
                </Link>
              </li>
              <li>
                <Link to="/terms-conditions" data-testid="footer-terms-link" className="text-sm text-slate-500 transition-colors hover:text-amber-400">
                  {t("footer.terms")}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-amber-500/10 pt-8 sm:flex-row">
          <p className="text-xs text-slate-500">{t("footer.copyright")}</p>
          <p className="text-[10px] uppercase tracking-[0.3em] text-slate-600">{t("footer.locationTag")}</p>
        </div>
      </div>
    </footer>
  );
};
